import React from 'react';
import { COLORS } from '../../../../core/constants/colors';

interface ScoreBreakdownProps {
  scoreBreakdown: any;
}

interface RuleResult {
  section?: string;
  field_id?: string;
  rule_id?: string;
  description: string;
  passed: boolean;
  message: string;
  field_score: number;
  weight: number;
}

export const ScoreBreakdownDisplay: React.FC<ScoreBreakdownProps> = ({ scoreBreakdown }) => {
  if (!scoreBreakdown || typeof scoreBreakdown !== 'object') {
    return (
      <div className="border rounded-lg p-4" style={{ borderColor: COLORS.extended.cultured }}>
        <pre className="text-sm overflow-x-auto">
          {JSON.stringify(scoreBreakdown, null, 2)}
        </pre>
      </div>
    );
  }

  const overallScore = scoreBreakdown.overall_score_percent;
  const totalAchieved = scoreBreakdown.total_achieved_points;
  const totalPossible = scoreBreakdown.total_possible_points;
  const formBreakdown = scoreBreakdown.form_score_breakdown;
  const fileBreakdown = scoreBreakdown.file_score_breakdown;
  const message = scoreBreakdown.message;

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#4CAF50';
    if (score >= 50) return '#FF9800';
    return '#F44336';
  };

  const calculatePercentage = (achieved: number, total: number) => {
    if (total === 0) return 0;
    return (achieved / total) * 100;
  };

  const groupRulesBySection = (rules: RuleResult[]) => {
    const grouped: Record<string, RuleResult[]> = {};
    rules.forEach(rule => {
      const section = rule.section || 'other';
      if (!grouped[section]) {
        grouped[section] = [];
      }
      grouped[section].push(rule);
    });
    return grouped;
  };

  return (
    <div className="space-y-6">
      {/* Overall Score Summary */}
      {overallScore !== undefined && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border" style={{ borderColor: COLORS.extended.cultured }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold" style={{ color: COLORS.primary.persianIndigo }}>
                Overall Score
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {totalAchieved} / {totalPossible} points achieved
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold" style={{ color: getScoreColor(overallScore) }}>
                {overallScore.toFixed(1)}%
              </div>
            </div>
          </div>
          <div className="bg-gray-200 rounded-full h-3">
            <div
              className="h-3 rounded-full transition-all"
              style={{
                width: `${overallScore}%`,
                backgroundColor: getScoreColor(overallScore),
              }}
            />
          </div>
          {message && (
            <div className="mt-4 p-3 rounded-lg bg-white bg-opacity-70">
              <p className="text-sm text-gray-700">{message}</p>
            </div>
          )}
        </div>
      )}

      {/* Form Score Breakdown */}
      {formBreakdown && (
        <div className="border rounded-lg p-4" style={{ borderColor: COLORS.extended.cultured }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold" style={{ color: COLORS.primary.persianIndigo }}>
              Form Score Breakdown
            </h3>
            <div className="text-sm text-gray-600">
              <span className="font-semibold" style={{ color: getScoreColor(calculatePercentage(formBreakdown.achieved_points, formBreakdown.total_possible_points)) }}>
                {formBreakdown.achieved_points}
              </span>
              {' / '}
              <span>{formBreakdown.total_possible_points} points</span>
            </div>
          </div>
          
          {formBreakdown.rule_results && formBreakdown.rule_results.length > 0 && (
            <div className="space-y-4">
              {Object.entries(groupRulesBySection(formBreakdown.rule_results)).map(([section, rules]) => (
                <div key={section} className="border-l-4 pl-4" style={{ borderColor: COLORS.primary.violet }}>
                  <h4 className="font-semibold text-sm uppercase mb-2" style={{ color: COLORS.primary.persianIndigo }}>
                    {section.replace('_', ' ')}
                  </h4>
                  <div className="space-y-3">
                    {rules.map((rule, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg ${
                          rule.passed ? 'bg-green-50' : 'bg-red-50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  rule.passed ? 'bg-green-500' : 'bg-red-500'
                                }`}
                              />
                              <span className="font-medium text-sm text-gray-800">
                                {rule.field_id || rule.rule_id}
                              </span>
                              {rule.passed ? (
                                <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                                  Passed
                                </span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                                  Failed
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mb-1">{rule.description}</p>
                            <p className={`text-xs ${rule.passed ? 'text-green-700' : 'text-red-700'}`}>
                              {rule.message}
                            </p>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
                              {rule.field_score} / {rule.weight}
                            </div>
                            <div className="text-xs text-gray-500">points</div>
                          </div>
                        </div>
                        {rule.weight > 0 && (
                          <div className="mt-2 bg-gray-200 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full transition-all"
                              style={{
                                width: `${(rule.field_score / rule.weight) * 100}%`,
                                backgroundColor: rule.passed ? '#4CAF50' : '#F44336',
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* File Score Breakdown */}
      {fileBreakdown && (
        <div className="border rounded-lg p-4" style={{ borderColor: COLORS.extended.cultured }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold" style={{ color: COLORS.primary.persianIndigo }}>
              File Score Breakdown
            </h3>
            <div className="text-sm text-gray-600">
              <span className="font-semibold" style={{ color: getScoreColor(calculatePercentage(fileBreakdown.achieved_points, fileBreakdown.total_possible_points)) }}>
                {fileBreakdown.achieved_points}
              </span>
              {' / '}
              <span>{fileBreakdown.total_possible_points} points</span>
            </div>
          </div>
          
          {fileBreakdown.rule_results && fileBreakdown.rule_results.length > 0 && (
            <div className="space-y-3">
              {fileBreakdown.rule_results.map((rule: RuleResult, index: number) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg ${
                    rule.passed ? 'bg-green-50' : 'bg-red-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            rule.passed ? 'bg-green-500' : 'bg-red-500'
                          }`}
                        />
                        <span className="font-medium text-sm text-gray-800">
                          {rule.rule_id}
                        </span>
                        {rule.passed ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                            Passed
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                            Failed
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mb-1">{rule.description}</p>
                      <p className={`text-xs ${rule.passed ? 'text-green-700' : 'text-red-700'}`}>
                        {rule.message}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-sm font-semibold" style={{ color: COLORS.primary.persianIndigo }}>
                        {rule.field_score} / {rule.weight}
                      </div>
                      <div className="text-xs text-gray-500">points</div>
                    </div>
                  </div>
                  <div className="mt-2 bg-gray-200 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{
                        width: `${(rule.field_score / rule.weight) * 100}%`,
                        backgroundColor: rule.passed ? '#4CAF50' : '#F44336',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

